import {
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { useState } from "react";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeliveredDate from "./DeliveredDate";

function ozonTabByState(state) {
  switch ((state || "").toLowerCase()) {
    case "awaiting_packaging":
      return "awaiting_packaging";
    case "ready_for_shipment":
      return "awaiting_deliver";
    case "delivered":
      return "delivered";
    case "cancelled":
      return "cancelled";
    default:
      return "fbs";
  }
}

export default function PostingsTable({
  rows,
  total,
  loading,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onSelect,
  status,
  market,
  selectedRowId,
}) {
  const isWb = (market || "").toLowerCase() === "wb";
  const baseColCount = 6; // Заказ, Товар, Ссылка, Дата заказа, КИЗ, Продажа/Возврат
  const colCount = isWb ? baseColCount + 2 : baseColCount;
  const [visitedLinks, setVisitedLinks] = useState(() => new Set());

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }} variant="outlined">
      {loading && (
        <div style={{ textAlign: "center", padding: 20 }}>
          <CircularProgress size={24} />
        </div>
      )}

      <TableContainer sx={{ maxHeight: 520 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Заказ</TableCell>
              <TableCell>Товар</TableCell>
              {isWb && <TableCell>WB Номер заказа</TableCell>}
              <TableCell>Ссылка</TableCell>
              <TableCell>Дата заказа</TableCell>
              {status === "delivered" ||
                (status === "withdrawal" && (
                  <TableCell>Дата доставки</TableCell>
                ))}
              <TableCell>КИЗ</TableCell>
              {isWb && (
                <TableCell align="center" width={140}>
                  КИЗ в WB
                </TableCell>
              )}
              {status === "cancelled" && <TableCell>Возврат</TableCell>}
              {status !== "cancelled" && (
                <TableCell align="center" width={140}>
                  Продажа
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row) => {
              const tab = ozonTabByState(row.state);
              const market = (row.market || "").toLowerCase();

              const href =
                market === "ozon"
                  ? `https://seller.ozon.ru/app/postings/fbs?tab=${encodeURIComponent(
                      tab,
                    )}&postingDetails=${encodeURIComponent(row.posting_number)}`
                  : market === "ym"
                    ? `https://partner.market.yandex.ru/business/216398963/orders?tabId=all&needOpenDrawerInfo=1&activeOrderId=216398962_${row.posting_number}&searchQuery=${row.posting_number}`
                    : market === "wb"
                      ? `https://seller.wildberries.ru/marketplace-orders-fbs/new-tasks`
                      : null;
              return (
                <TableRow
                  key={row.id}
                  hover
                  selected={selectedRowId === row.id}
                  sx={{ cursor: "pointer" }}
                  onClick={() => onSelect?.(row)}
                >
                  <TableCell>{row.posting_number}</TableCell>

                  <TableCell>
                    {row.product_title || row.article || "—"}
                  </TableCell>

                  {isWb && <TableCell>{row.wbOrderId || "—"}</TableCell>}

                  <TableCell>
                    {href ? (
                      // Цвет иконки меняется на зелёный, если переход уже совершали в этой сессии
                      <IconButton
                        size="small"
                        color={visitedLinks.has(row.id) ? "success" : "default"}
                        onClick={(e) => {
                          e.stopPropagation();
                          setVisitedLinks((prev) => new Set([...prev, row.id]));
                          window.open(href, "_blank", "noopener,noreferrer");
                        }}
                        aria-label="Открыть ссылку постинга"
                      >
                        <OpenInNewIcon fontSize="inherit" />
                      </IconButton>
                    ) : (
                      <span>-</span>
                    )}
                  </TableCell>

                  <TableCell>
                    {row.in_process_at
                      ? new Date(row.in_process_at).toLocaleString()
                      : ""}
                  </TableCell>
                  {status === "delivered" ||
                    (status === "withdrawal" && (
                      <TableCell>
                        <DeliveredDate value={row.in_process_at} />
                      </TableCell>
                    ))}
                  <TableCell>
                    {row.kiz_status === "complete" ? (
                      <Chip size="small" label="Добавлен" color="success" />
                    ) : (
                      <Chip size="small" label="Не добавлен" />
                    )}
                  </TableCell>
                  {isWb && (
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={row.kiz_sent ? "Отправлен" : "Не отправлен"}
                        color={row.kiz_sent ? "success" : "default"}
                        variant={row.kiz_sent ? "filled" : "outlined"}
                      />
                    </TableCell>
                  )}
                  {status === "cancelled" && (
                    <TableCell>
                      {row.return_status === "returned_all" ? (
                        <Chip size="small" label="Возвращён" color="success" />
                      ) : row.return_status === "need_return" ? (
                        <Chip
                          size="small"
                          label="Надо вернуть"
                          color="warning"
                        />
                      ) : (
                        <Chip size="small" label="—" />
                      )}
                    </TableCell>
                  )}
                  {status !== "cancelled" && (
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={
                          row.withdrawal_status === "sold"
                            ? "Продан"
                            : "На балансе"
                        }
                        color={
                          row.withdrawal_status === "sold" ? "success" : "error"
                        }
                        variant={
                          row.withdrawal_status === "sold"
                            ? "filled"
                            : "outlined"
                        }
                      />
                    </TableCell>
                  )}
                </TableRow>
              );
            })}

            {rows.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={colCount} align="center" sx={{ py: 3 }}>
                  Нет данных
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, newPage) => onPageChange(newPage)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) =>
          onPageSizeChange(parseInt(e.target.value, 10))
        }
        rowsPerPageOptions={[10, 20, 50]}
      />
    </Paper>
  );
}
